import { useState, useEffect, useRef } from 'react';
import { generateContractPDF, downloadPDF } from '../pdfGen';
import { sendContractEmailViaBackend, createZohoContactViaBackend } from '../api';

// Silently push to Zoho via Flask backend — fires and forgets
async function pushToZoho(formData, setZohoError) {
  try {
    const result = await createZohoContactViaBackend(formData);
    if (!result.ok && !result.existed) {
      setZohoError && setZohoError(result.error || 'Zoho contact creation failed');
    }
  } catch (e) {
    console.error('Zoho push failed:', e.message);
    setZohoError && setZohoError(e.message);
  }
}

export default function StepSend({ data, onBack, user }) {
  const [action, setAction]         = useState('download');
  const [sendEmail, setSendEmail]   = useState(data.custEmail || '');
  const [done, setDone]             = useState(false);
  const [sending, setSending]       = useState(false);
  const [emailError, setEmailError] = useState('');
  const [zohoError, setZohoError]   = useState('');
  const pdfRef = useRef(null);

  useEffect(() => {
    pdfRef.current = generateContractPDF(data);
    setSendEmail(data.custEmail || '');
  }, [data]);

  const handleDownload = () => {
    downloadPDF(pdfRef.current, data.custName);
    setDone(true);
    pushToZoho(data, setZohoError);
  };

  const handleEmail = async () => {
    if (!sendEmail) return;
    setSending(true);
    setEmailError('');
    try {
      await sendContractEmailViaBackend({
        formData: { ...data, custEmail: sendEmail, senderEmail: user?.email || 'sales@softglowlab.com' },
        pdfDoc:   pdfRef.current,
      });
      setDone(true);
      pushToZoho(data, setZohoError);
    } catch (err) {
      setEmailError(err.message || 'Failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="card">
        <div className="card-title">
          Document ready
          <span className="badge">PDF</span>
        </div>

        <div className="action-grid">
          <div
            className={`action-card${action === 'download' ? ' selected' : ''}`}
            onClick={() => { setAction('download'); setEmailError(''); }}
          >
            <span className="action-icon">⬇</span>
            <h4>Download PDF</h4>
            <p>Save the contract to your device</p>
          </div>
          <div
            className={`action-card${action === 'email' ? ' selected' : ''}`}
            onClick={() => { setAction('email'); setEmailError(''); }}
          >
            <span className="action-icon">✉</span>
            <h4>Email to customer</h4>
            <p>Send from sales@softglowlab.com with PDF attached</p>
          </div>
        </div>

        {action === 'email' && (
          <div style={{ marginTop: 14 }}>
            <div className="field" style={{ marginBottom: 10 }}>
              <label>Recipient email</label>
              <input
                type="email"
                value={sendEmail}
                onChange={e => setSendEmail(e.target.value)}
                placeholder="Customer email address"
              />
            </div>
            <div className="alert alert-info" style={{ marginTop: 4, marginBottom: 0 }}>
              <span className="alert-icon">✉</span>
              <div className="alert-body">
                <p>Sent from <strong>sales@softglowlab.com</strong> with PDF attached. A copy goes to <strong>{user?.email || 'sales@softglowlab.com'}</strong>.<br/><span style={{fontSize:11,color:'var(--muted)'}}>Brands: {(data.brands || ['Revitalash']).join(', ')}</span></p>
              </div>
            </div>
            {emailError && (
              <div className="alert alert-error" style={{ marginTop: 8, marginBottom: 0 }}>
                <span className="alert-icon">✕</span>
                <div className="alert-body"><p>{emailError}</p></div>
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          {action === 'download' ? (
            <button className="btn btn-primary" onClick={handleDownload}>
              Download PDF
            </button>
          ) : (
            <button
              className="btn btn-success"
              onClick={handleEmail}
              disabled={!sendEmail || sending}
            >
              {sending ? <><span className="spinner" />Sending…</> : 'Send email'}
            </button>
          )}
        </div>
      </div>

      {zohoError && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>
          <div className="alert-body">
            <h4>Zoho sync failed</h4>
            <p>{zohoError}</p>
          </div>
        </div>
      )}

      {done && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          <div className="alert-body">
            <h4>{action === 'download' ? 'PDF downloaded' : 'Email sent'}</h4>
            <p>
              {action === 'download'
                ? `${data.custName}'s contract saved. Print, sign, and send for counter-signature.`
                : `Contract sent to ${sendEmail}. A copy has been sent to ${user?.email || 'sales@softglowlab.com'}.`}
            </p>
          </div>
        </div>
      )}

      <div className="row between" style={{ marginTop: 8 }}>
        <button className="btn btn-ghost" onClick={onBack}>← Back</button>
        <button className="btn btn-ghost" onClick={() => window.location.reload()}>
          New contract
        </button>
      </div>
    </div>
  );
}
